import { useState } from 'react';
import { useStringsContext } from '../../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { QuizResult } from '../../types';
import BaseDialog from '../../../../common/base-dialog';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { SxStyle } from '../../../../../types';

const styles: SxStyle = {
  listItem: (theme) => ({
    borderBottom: `1px solid ${theme.palette.grey[300]}`,

    '&:last-child': {
      borderBottom: 0,
    },
  }),
  listItemText: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px',
  },
  tableCell: {
    border: 0,
    paddingTop: '2px',
    paddingBottom: '2px',
  },
};

interface PollVoterItemProps {
  item: QuizResult;
  index: number;
}

const QuizItem = ({ item, index }: PollVoterItemProps) => {
  const { strings } = useStringsContext();
  const [showMore, setShowMore] = useState(false);

  return (
    <ListItem key={item.userId} sx={styles.listItem} disablePadding>
      <Stack direction="column" flex={1}>
        <Stack direction="row" sx={styles.listItemText}>
          <ListItemText
            primaryTypographyProps={{ variant: 'body2' }}
            primary={strings.polls.quizDetailsTitle
              .replace('$number', String(index + 1))
              .replace('$score', String(item.score))}
          />
          <Link
            sx={{ textDecoration: 'underline' }}
            component="button"
            variant="body2"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? strings.actions.hide : strings.actions.show}
          </Link>
        </Stack>
        <Collapse in={showMore}>
          <Table size="small" sx={{ marginBottom: '2px' }}>
            <TableBody>
              <TableRow>
                <TableCell sx={styles.tableCell} width={130} align="left">
                  <b>{strings.polls.votersName}</b>
                </TableCell>
                {item.name && item.name.trim().length > 0 ? (
                  <TableCell sx={styles.tableCell} align="left">
                    {item.name}
                  </TableCell>
                ) : (
                  <TableCell sx={styles.tableCell} align="left">
                    <i>{strings.polls.votersNameMissing}</i>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={styles.tableCell} width={130} align="left">
                  <b>{strings.polls.votersEmail}</b>
                </TableCell>
                <TableCell sx={styles.tableCell} align="left">
                  <Link sx={{ textDecoration: 'underline' }} href={`mailto: ${item.email}`}>
                    {item.email}
                  </Link>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Collapse>
      </Stack>
    </ListItem>
  );
};

interface Props {
  open: boolean;
  items: QuizResult[];
  onClose: () => void;
}

const PollQuizDetailsModal = ({ open, items, onClose }: Props) => {
  const { strings } = useStringsContext();
  const sortedItems = items.sort((a, b) => b.score - a.score);

  return (
    <BaseDialog open={open} title={strings.polls.votersTitle} onClose={onClose}>
      <DialogContent dividers sx={{ paddingTop: 0, paddingBottom: 0 }}>
        <List disablePadding>
          {sortedItems.map((item, index) => (
            <QuizItem key={item.score} index={index} item={item} />
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          {strings.actions.close}
        </Button>
      </DialogActions>
    </BaseDialog>
  );
};

export default PollQuizDetailsModal;
